import { useMemo, useState } from 'react'
import {
    GearIcon,
    BookOpenIcon,
    SlidersHorizontalIcon,
    MagnifyingGlassIcon,
    ClockCounterClockwiseIcon,
    PlusIcon,
    FileIcon,
} from '@phosphor-icons/react'
import { ScoopText, ScoopButton, ScoopTextField, ScoopColumn } from '../../../../ui'
import styles from './ConfigurationNav.module.scss'

const NAV_ITEMS = {
    configuration: {
        label: 'Configuration',
        items: [
            {
                key: 'overview',
                label: 'Overview',
                icon: GearIcon,
                filePath: 'metadata.json',
            },
            {
                key: 'definitions',
                label: 'Business Terms',
                icon: BookOpenIcon,
                filePath: 'definitions.json',
            },
            {
                key: 'orchestration',
                label: 'Settings',
                icon: SlidersHorizontalIcon,
                filePath: 'orchestration.json',
            },
            {
                key: 'query_patterns',
                label: 'Query Patterns',
                icon: MagnifyingGlassIcon,
                filePath: 'query_patterns.json',
            },
        ],
    },
    history: {
        label: 'History',
        items: [
            {
                key: 'learning_history',
                label: 'Change Log',
                icon: ClockCounterClockwiseIcon,
                filePath: 'learning_history.json',
            },
        ],
    },
}

export const ConfigurationNav = ({ files, selectedItem, onItemSelect, onCreateContext }) => {
    const [contextSearch, setContextSearch] = useState('')

    // Extract contexts from files
    const contexts = useMemo(() => {
        return files
            .filter(
                (file) => file.path.startsWith('contexts/') && !file.path.endsWith('_index.json')
            )
            .map((file) => {
                const fileName = file.path.split('/').pop()
                const name = fileName.replace('.json', '')
                // Convert snake_case to Title Case for display
                const displayName = name
                    .split('_')
                    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')
                return {
                    key: `context:${name}`,
                    name,
                    displayName,
                    filePath: file.path,
                }
            })
            .sort((a, b) => a.displayName.localeCompare(b.displayName))
    }, [files])

    // Filter contexts based on search
    const filteredContexts = useMemo(() => {
        if (!contextSearch.trim()) return contexts
        const search = contextSearch.toLowerCase()
        return contexts.filter(
            (ctx) =>
                ctx.displayName.toLowerCase().includes(search) ||
                ctx.name.toLowerCase().includes(search)
        )
    }, [contexts, contextSearch])

    // Check which files exist
    const fileExists = useMemo(() => {
        const existingPaths = new Set(files.map((f) => f.path))
        return (path) => existingPaths.has(path)
    }, [files])

    const renderNavItem = (item) => {
        const isSelected = selectedItem === item.key
        const isVirtualItem = item.filePath == null
        const exists = isVirtualItem ? true : fileExists(item.filePath)
        const IconComponent = item.icon

        return (
            <button
                key={item.key}
                className={`${styles.navItem} ${isSelected ? styles.selected : ''} ${!exists ? styles.missing : ''}`}
                onClick={() => onItemSelect(item.key, item.filePath)}
                type="button"
                title={!exists ? 'File not found - click to initialize' : undefined}
            >
                <IconComponent
                    size={18}
                    weight={isSelected ? 'fill' : 'regular'}
                    className={styles.navIcon}
                />
                <ScoopText className={styles.navLabel}>{item.label}</ScoopText>
            </button>
        )
    }

    const renderContextItem = (context) => {
        const isSelected = selectedItem === context.key

        return (
            <button
                key={context.key}
                className={`${styles.navItem} ${styles.contextItem} ${isSelected ? styles.selected : ''}`}
                onClick={() => onItemSelect(context.key, context.filePath)}
                type="button"
            >
                <FileIcon
                    size={18}
                    weight={isSelected ? 'fill' : 'regular'}
                    className={styles.navIcon}
                />
                <ScoopText className={styles.navLabel}>{context.displayName}</ScoopText>
            </button>
        )
    }

    const renderSection = (sectionKey, section) => (
        <div key={sectionKey} className={styles.section}>
            <div className={styles.sectionHeader}>
                <ScoopText className={styles.sectionLabel}>{section.label}</ScoopText>
            </div>
            <ScoopColumn className={styles.sectionContent}>
                {section.items.map(renderNavItem)}
            </ScoopColumn>
        </div>
    )

    return (
        <div className={styles.container}>
            <ScoopColumn className={styles.content}>
                {/* Configuration Section */}
                {renderSection('configuration', NAV_ITEMS.configuration)}

                {/* Contexts Section */}
                <div className={styles.section}>
                    <div className={styles.sectionHeader}>
                        <ScoopText className={styles.sectionLabel}>
                            Investigation Contexts
                        </ScoopText>
                    </div>
                    {contexts.length > 0 && (
                        <div className={styles.searchWrapper}>
                            <ScoopTextField
                                value={contextSearch}
                                onChange={(e) => setContextSearch(e.target.value)}
                                placeholder="Search contexts..."
                                size="sm"
                            />
                        </div>
                    )}
                    <div className={styles.contextsList}>
                        {contexts.length > 0 ? (
                            filteredContexts.length > 0 ? (
                                filteredContexts.map(renderContextItem)
                            ) : (
                                <ScoopText className={styles.emptyContexts}>
                                    No contexts match your search
                                </ScoopText>
                            )
                        ) : (
                            <ScoopText className={styles.emptyContexts}>
                                No contexts created yet
                            </ScoopText>
                        )}
                    </div>
                    <div className={styles.createButtonWrapper}>
                        <ScoopButton
                            variant="grey"
                            size="xs"
                            leftSection={<PlusIcon size={14} />}
                            onClick={onCreateContext}
                            className={styles.createButton}
                        >
                            Create New
                        </ScoopButton>
                    </div>
                </div>

                {renderSection('history', NAV_ITEMS.history)}
            </ScoopColumn>
        </div>
    )
}

// Export the nav item mapping for use in the main component
export const getNavItemInfo = (selectedItem) => {
    // Check configuration items
    for (const section of Object.values(NAV_ITEMS)) {
        const item = section.items.find((i) => i.key === selectedItem)
        if (item) {
            return item.filePath != null
                ? { type: item.key, filePath: item.filePath }
                : { type: item.key }
        }
    }

    // Check if it's a context
    if (selectedItem?.startsWith('context:')) {
        const contextName = selectedItem.replace('context:', '')
        return { type: 'context', contextName, filePath: `contexts/${contextName}.json` }
    }

    return null
}
